/*
This is a custom axios instance that is used to make requests to the backend.
This is what we import into components rather than the default axios instance.
*/

import axios from "axios";
import AuthService from "./services/AuthService";
import TokenService from "./services/TokenService";
import { TOKEN_NOT_VALID, USER_INACTIVE } from "./codes";
import { LOGIN } from "../routes/paths";

const baseUrl = process.env.REACT_APP_BACKEND_URL;

const instance = axios.create({
  baseURL: `${baseUrl}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
});

/* pre-request handler */
instance.interceptors.request.use(
  function (config) {
    // Do something before request is sent
    const token = TokenService.getLocalAccessToken();
    if (token) {
      // @ts-ignore
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

/* post-request handler */
instance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  // handle errors
  async function (error) {
    let originalConfig = error.config;
    const errorResponse = error.response;
    const errorData = errorResponse?.data;

    if (errorResponse && errorData && errorData.code === USER_INACTIVE) {
      // logout user if they are inactive
      AuthService.logout();
      window.location.href = LOGIN;
    }

    // only retry if we haven't retried yet (avoids infinite loop)
    if (originalConfig && !originalConfig._retry) {
      // only do it if it is a the right error code
      if (errorResponse && errorData && errorData.code === TOKEN_NOT_VALID) {
        // avoid infinite loop if refresh token is expired
        // the only way to get a new one is do login again
        if (
          originalConfig?.url &&
          originalConfig?.url.includes("/auth/token/refresh")
        ) {
          AuthService.logout();
          window.location.href = LOGIN;
          return Promise.reject(error);
        }
        // try getting a new refresh token
        originalConfig._retry = true;
        const success = await AuthService.getRefreshToken();
        if (success) {
          return instance(originalConfig);
        }
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default instance;
